import { useState, useEffect, useRef } from 'react';
import Modal from '../core/Modal';
import ForgotPasswordModal from './ForgotPasswordModal';
import { useTranslation } from '../../i18n/useTranslation';
import useInstallPrompt from '../../hooks/useInstallPrompt';
import { resolveGateMessage } from '../../i18n/gateMessages';

// Use raw client for auth operations (login/register bypass protected wrapper)
import { supabaseRaw as supabase, supabaseFetch } from '../../lib/supabase';
import { addBreadcrumb, addCaughtError } from '../../utils/errorCapture';
import { groupService } from '../../data/dataService';
import { authErrorMessage, buildSignupRedirect, RESEND_WAIT_SECONDS } from '../../utils/authFlow';
import { PASSWORD_MIN_LENGTH, USER_DISPLAY_NAME_MAX } from '../../utils/validationConstants';

// Registration confirm-first (Roadmap #88; docs/briefs/2026-10-03-registration-confirm-first.md +
// addenda 1 and 2), 3 Oct 2026. Registering is now one screen — email, display name, password — and the
// account is created when it is submitted (R1); then "check your email" (R3). Location, area and the
// optional profile happen after confirming, signed in (AccountSetup, mounted by App). The old
// three-step wizard, its browser stash and its "Registration successful" dialogue are retired.
// Errors are translated from Supabase's error codes and shown at the field they concern (R6;
// addendum 1 item 4). An invitation the person arrived with rides in the confirmation link
// (inviteContext; Invitations Build 2 §1–§2, built here per addendum 1 item 3), and a personal
// invitation's address is filled in (still editable). Sign-in refused because the email is not yet
// confirmed offers to resend the confirmation email there and then (addendum 2 item 2).

const emptyErrors = { email: null, userName: null, password: null, confirmPassword: null, terms: null, general: null };

const LoginRegisterModal = ({
  visible = false,
  onClose = null,
  onLoginSuccess = null,
  setIsLoggedIn = null,
  initialMode = 'login', // 'login' | 'register' — which view to open on
  gateMessage = null,    // reassurance copy (gate key or literal) when opened from an auth-gated action; null → generic contribution prompt
  inviteContext = null   // { type: 'invite' | 'join', token } — the invitation this person arrived with, if any
}) => {
  const { t } = useTranslation('auth');
  const [mode, setMode] = useState(initialMode === 'register' ? 'register' : 'login');
  const [formData, setFormData] = useState({
    email: '',
    userName: '',
    password: '',
    confirmPassword: ''
  });
  const [agreeToTerms, setAgreeToTerms] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState(null);             // sign-in: one auth-namespace key, translated at render
  const [fieldErrors, setFieldErrors] = useState(emptyErrors); // register: one message per field
  const [loading, setLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  // After a successful sign-up: the address the confirmation went to (the "check your email" panel).
  const [sentTo, setSentTo] = useState(null);
  const [resendWait, setResendWait] = useState(0);
  const [resendNote, setResendNote] = useState(null);   // 'check_email_resent' | an error key
  // Sign-in refused as "email not confirmed": the address that was refused, for the resend offer.
  const [unconfirmedEmail, setUnconfirmedEmail] = useState(null);

  // Load saved email on mount (not password - Supabase handles session)
  useEffect(() => {
    const savedEmail = localStorage.getItem('ournet_saved_email');
    if (savedEmail) {
      setFormData(prev => ({
        ...prev,
        email: savedEmail
      }));
      setRememberMe(true);
    }
  }, []);

  // Honour the requested initial view only on the modal's closed→open transition,
  // so it never fights the user's own login↔register toggle (switchMode) while open.
  const prevVisibleRef = useRef(false);
  useEffect(() => {
    if (visible && !prevVisibleRef.current) {
      setMode(initialMode === 'register' ? 'register' : 'login');
      setSentTo(null);
      setFieldErrors(emptyErrors);
      setError(null);
      setUnconfirmedEmail(null);
      setResendNote(null);
      // A personal invitation's address wins over a remembered sign-in email (Build 2 §2). Display
      // only — acceptance is governed by the invitation, not this field (R3 of the invitations rulings).
      if (inviteContext?.type === 'invite') {
        const invited = groupService.decodeInvitationToken(inviteContext.token)?.email;
        if (invited) setFormData(prev => ({ ...prev, email: invited }));
      }
      // This modal is mounted unconditionally (App.js), so nothing unmounts it and every
      // piece of local state survives a close. An eye left open — here or in the register
      // branch, one flag drives all three fields — would otherwise leave #signin-password
      // as type="text" on the next open, so the form would present NO type="password"
      // field to a password manager. Desktop extensions key on that type first (Android's
      // Autofill Framework reads the autocomplete hint, which is why the phone was fine).
      // Brief: docs/briefs/2026-09-26-bitwarden-desktop-email-in-password-box.md.
      setShowPassword(false);
    }
    prevVisibleRef.current = visible;
  }, [visible, initialMode]); // eslint-disable-line react-hooks/exhaustive-deps

  // The resend button's wait (Auth's 60-second minimum between confirmation emails).
  useEffect(() => {
    if (resendWait <= 0) return undefined;
    const timer = setTimeout(() => setResendWait(s => s - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendWait]);

  const innerRef = useRef(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError(null);
    // An error belongs to its field: editing the field clears it (and any general message).
    setFieldErrors(prev => ({ ...prev, [name]: null, general: null }));
    // The resend offer belongs to the address that was refused, not to whatever is typed next.
    if (name === 'email') { setUnconfirmedEmail(null); setResendNote(null); }
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Registration's own checks, each at its field. The password is checked for length only (the
  // shared minimum, addendum 2 item 1); every other password rule comes back from Supabase as an
  // error code.
  const validateRegistration = () => {
    const next = { ...emptyErrors };
    if (!formData.email.trim()) next.email = 'email_required';
    else if (!validateEmail(formData.email.trim())) next.email = 'email_invalid';
    if (!formData.userName.trim()) next.userName = 'username_required';
    if (!formData.password) next.password = 'password_required';
    else if (formData.password.length < PASSWORD_MIN_LENGTH) next.password = 'password_min_length';
    if (formData.password && formData.password !== formData.confirmPassword) next.confirmPassword = 'passwords_no_match';
    if (!agreeToTerms) next.terms = 'must_agree_terms';
    setFieldErrors(next);
    return !Object.values(next).some(Boolean);
  };

  const showAuthError = (err, context) => {
    const mapped = authErrorMessage(err, context);
    if (!mapped.known) {
      // Raw text to the error log only, never to the screen (R6).
      addCaughtError(err?.message || String(err), `LoginRegisterModal:${context}`);
    }
    return mapped;
  };

  const handleRegister = async () => {
    if (!validateRegistration()) return;

    setLoading(true);
    addBreadcrumb('auth', { action: 'register-attempt', component: 'LoginRegisterModal' });
    const email = formData.email.trim().toLowerCase();

    try {
      // The confirmation link brings the person back signed in — carrying their invitation, if any.
      const { data, error: signUpError } = await supabase.auth.signUp({
        email,
        password: formData.password,
        options: {
          data: {
            display_name: formData.userName.trim()
          },
          emailRedirectTo: buildSignupRedirect(window.location.origin, inviteContext)
        }
      });

      if (signUpError) {
        const mapped = showAuthError(signUpError, 'register');
        setFieldErrors({ ...emptyErrors, [mapped.field]: mapped.key });
        return;
      }

      // An address already registered comes back with no identities (and no email is sent).
      if (data?.user?.identities?.length === 0) {
        setFieldErrors({ ...emptyErrors, email: 'account_exists' });
        return;
      }

      // Honest wording: nothing is "successful" until the person confirms (R3).
      setSentTo(email);
      setResendWait(RESEND_WAIT_SECONDS);
      setResendNote(null);
      setFormData(prev => ({ ...prev, password: '', confirmPassword: '' }));
    } catch (err) {
      const mapped = showAuthError(err, 'register');
      setFieldErrors({ ...emptyErrors, [mapped.field]: mapped.key });
    } finally {
      setLoading(false);
    }
  };

  // Sync submit wrapper for the register <form>, for the same reason as the login one below.
  const handleRegisterSubmit = (e) => {
    e.preventDefault();
    handleRegister();
  };

  // Resend the confirmation email — from the "check your email" panel (to sentTo) or from sign-in
  // (to the address that was refused as not confirmed). Same 60-second wait either way.
  const handleResend = async (address) => {
    if (!address || resendWait > 0 || loading) return;
    setLoading(true);
    setResendNote(null);
    try {
      const { error: resendError } = await supabase.auth.resend({
        type: 'signup',
        email: address,
        options: { emailRedirectTo: buildSignupRedirect(window.location.origin, inviteContext) }
      });
      if (resendError) {
        setResendNote(showAuthError(resendError, 'register').key);
      } else {
        setResendNote('check_email_resent');
      }
      setResendWait(RESEND_WAIT_SECONDS);
    } catch (err) {
      setResendNote(showAuthError(err, 'register').key);
    } finally {
      setLoading(false);
    }
  };

  // "Wrong address? Start again": back to the form, the address kept to correct.
  const handleStartAgain = () => {
    setFormData(prev => ({ ...prev, email: sentTo || prev.email, password: '', confirmPassword: '' }));
    setSentTo(null);
    setResendNote(null);
    setFieldErrors(emptyErrors);
  };

  const handleLogin = async () => {
    setError(null);
    setUnconfirmedEmail(null);
    setResendNote(null);

    // Validation
    if (!formData.email.trim()) {
      setError('email_required');
      return;
    }

    if (!validateEmail(formData.email)) {
      setError('email_invalid');
      return;
    }

    if (!formData.password) {
      setError('password_required');
      return;
    }

    setLoading(true);
    addBreadcrumb('auth', { action: 'login-attempt', component: 'LoginRegisterModal' });

    try {
      // Use Supabase Auth signInWithPassword
      const { data, error: signInError } = await supabase.auth.signInWithPassword({
        email: formData.email.trim().toLowerCase(),
        password: formData.password
      });

      if (signInError) {
        // By error code, never by Supabase's English wording (addendum 1, item 4).
        const mapped = showAuthError(signInError, 'login');
        if (mapped.key === 'confirm_email_first') setUnconfirmedEmail(formData.email.trim().toLowerCase());
        setError(mapped.key);
        return;
      }

      const authUser = data.user;
      console.log('✅ User logged in successfully:', authUser.email);

      // Fetch additional profile data from public.users
      const { data: profileData } = await supabaseFetch.query(
        'users',
        { select: 'display_name, bio, avatar_url', eq: { id: authUser.id }, single: true }
      );

      // Handle Remember Me - only save email (Supabase handles session tokens)
      if (rememberMe) {
        localStorage.setItem('ournet_saved_email', formData.email.trim().toLowerCase());
      } else {
        localStorage.removeItem('ournet_saved_email');
      }
      // Remove any old password storage
      localStorage.removeItem('ournet_saved_password');

      // Build user object with merged data
      const user = {
        id: authUser.id,
        email: authUser.email,
        display_name: profileData?.display_name || authUser.user_metadata?.display_name || authUser.email,
        avatar_url: profileData?.avatar_url || null
      };

      // Call success callback
      if (onLoginSuccess) {
        onLoginSuccess(user);
      }

      // Update logged in state
      if (setIsLoggedIn) {
        setIsLoggedIn(true);
      }

      // Close modal
      if (onClose) {
        onClose();
      }

      // Reset form
      setFormData({
        email: '',
        userName: '',
        password: '',
        confirmPassword: ''
      });
      setRememberMe(false);

    } catch (err) {
      console.error('Login error:', err);
      setError(showAuthError(err, 'login').key);
    } finally {
      setLoading(false);
    }
  };

  // Sync submit wrapper for the login <form>. preventDefault MUST be the first statement:
  // handleLogin is async, so calling it directly from onSubmit would let the browser submit
  // natively (method=GET, no action => current URL) and write name="email" and name="password"
  // into the URL and browser history in cleartext before any await resolved.
  const handleLoginSubmit = (e) => {
    e.preventDefault();
    handleLogin();
  };

  const handleBack = () => {
    if (onClose) {
      onClose();
    }
  };

  // "Install the app" for signed-out visitors, who can no longer open ProfileMenu. Same shared
  // hook and same availability test as ProfileMenu's Install item (canInstall: a real deferred
  // prompt is held and the app is not already installed). Brief:
  // docs/briefs/2026-09-26-logged-out-header-gap-and-install-link.md.
  const { canInstall, install } = useInstallPrompt();

  const switchMode = () => {
    setMode(mode === 'register' ? 'login' : 'register');
    setError(null);
    setFieldErrors(emptyErrors);
    setSentTo(null);
    setUnconfirmedEmail(null);
    setResendNote(null);
    setFormData({
      email: formData.email,
      userName: '',
      password: '',
      confirmPassword: ''
    });
    setAgreeToTerms(false);
    setRememberMe(false);
    // The password values are cleared just above, so a still-open eye would only reveal the
    // other branch's empty field — but it would also carry type="text" onto the field the
    // next surface shows. Reset with its siblings (same reason as the closed→open effect).
    setShowPassword(false);
  };

  const inputClass = 'w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent';
  const borderFor = (field) => ({ borderColor: fieldErrors[field] ? '#ef4444' : '#d1d5db' });
  // A plain render function, not a component: a component defined inside render would be a new
  // type every render, remounting each error (and re-announcing its role="alert") on every keystroke.
  const renderFieldError = (field) => (fieldErrors[field] ? (
    <p id={`register-${field}-error`} role="alert" className="text-sm mt-1" style={{ color: '#ef4444' }}>
      {t(fieldErrors[field], { min: PASSWORD_MIN_LENGTH })}
      {fieldErrors[field] === 'account_exists' && (
        <>
          {' '}
          <button
            type="button"
            onClick={switchMode}
            className="font-semibold underline"
            style={{ color: '#024B99', background: 'transparent', border: 'none', cursor: 'pointer', padding: 0, fontSize: 'inherit' }}
          >
            {t('log_in')}
          </button>
        </>
      )}
    </p>
  ) : null);

  // The resend note and button, shared by the "check your email" panel and the sign-in refusal.
  // The note sits outside any error box: "sent again" is good news on both screens.
  const renderResend = (address, label) => (
    <>
      {resendNote && (
        <p role="status" className="text-sm mb-3" style={{ color: resendNote === 'check_email_resent' ? '#059669' : '#ef4444' }}>
          {t(resendNote)}
        </p>
      )}
      <button
        type="button"
        onClick={() => handleResend(address)}
        disabled={resendWait > 0 || loading}
        className="py-3 px-6 rounded-lg font-semibold text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        style={{ background: '#5DBEA3', border: 'none' }}
      >
        {loading
          ? t('common:please_wait')
          : resendWait > 0 ? t('check_email_resend_wait', { seconds: resendWait }) : label}
      </button>
    </>
  );

  // Logo, title and prompt belong to the first screen only (R9): the "check your email" panel
  // carries just its own title.
  const showHeadings = !(mode === 'register' && sentTo);

  return (
    <>
      <Modal
        isOpen={visible}
        onClose={handleBack}
        alignTop
        closeOnBackdrop={(e) => {
          if (!innerRef.current) return true;
          const rect = innerRef.current.getBoundingClientRect();
          // ~5cm at 96dpi; scale down on smaller screens
          const bufferPx = Math.min(100, window.innerWidth * 0.06);
          return !(
            e.clientX > rect.left  - bufferPx &&
            e.clientX < rect.right + bufferPx &&
            e.clientY > rect.top   - bufferPx &&
            e.clientY < rect.bottom + bufferPx
          );
        }}
        panelStyle={{
          backgroundColor: 'white',
          borderRadius: '24px',
          boxShadow: '0 25px 50px rgba(0, 0, 0, 0.25)',
          width: '100%',
          // The map moved out of this modal (it lives in AccountSetup now), so both modes are the
          // narrow login width again.
          maxWidth: '420px',
          margin: '0 16px'
        }}
      >
        {/* inner div fills the panel exactly — ref used for the ~5cm backdrop-click buffer */}
        <div ref={innerRef} style={{ padding: '32px' }}>

        {showHeadings && (
          <>
            {/* OurNet Logo */}
            <div className="flex justify-center mb-6">
              <img
                src="/images/logos/logo-ournet.png"
                alt="OurNet"
                style={{ width: '80px', height: '80px', objectFit: 'contain' }}
              />
            </div>

            {/* Title */}
            <h2 className="text-2xl font-bold text-center mb-2" style={{ color: '#024B99' }}>
              {mode === 'register' ? t('register_title') : t('login_title')}
            </h2>

            {/* Register prompt - show on login mode */}
            {mode === 'login' && (
              <div className="text-center mb-2" style={{ color: '#024B99', fontSize: '16px' }}>
                {t('or')}{' '}
                <button
                  type="button"
                  onClick={switchMode}
                  className="font-semibold hover:underline"
                  style={{ color: '#024B99', background: 'transparent', border: 'none', cursor: 'pointer', padding: 0, fontSize: '16px' }}
                >
                  {t('register').toUpperCase()}
                </button>
              </div>
            )}

            {/* Subtitle — action-specific reassurance when opened from an auth gate,
                otherwise the generic contribution prompt (direct logins). Resolved via the
                shared gate-message helper so the copy never drifts from the old gate modal. */}
            <div className="text-center mb-6" style={{ color: '#024B99', fontSize: '14px', lineHeight: '1.5' }}>
              <p>{gateMessage ? resolveGateMessage(gateMessage, t) : t('contribution_prompt')}</p>
            </div>
          </>
        )}

        {/* Sign-in error message */}
        {mode === 'login' && error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
            {error === 'invalid_credentials' ? (
              <>
                {t('invalid_credentials')}{'. '}
                {t('no_account_hint')}{' '}
                <button
                  type="button"
                  onClick={switchMode}
                  className="font-semibold underline"
                  style={{ color: '#024B99', background: 'transparent', border: 'none', cursor: 'pointer', padding: 0, fontSize: 'inherit' }}
                >
                  {t('register')}
                </button>
              </>
            ) : t(error)}
          </div>
        )}

        {/* Not confirmed yet: a new confirmation link there and then (addendum 2 item 2). */}
        {mode === 'login' && error === 'confirm_email_first' && unconfirmedEmail && (
          <div className="mb-4 text-center">
            {renderResend(unconfirmedEmail, t('signin_resend_confirmation'))}
          </div>
        )}

        {/* Login — fields and actions inside ONE <form> so the browser and password
            managers see a real submission: that is what cues Chrome and Android autofill to
            offer to save, and later to fill (docs/briefs/2026-09-25-sign-in-forms-password-managers.md).
            onSubmit calls a SYNCHRONOUS wrapper whose first statement is preventDefault() —
            handleLogin is async, so passing it directly would let the browser submit natively
            as GET and write email and password into the URL and browser history.
            noValidate keeps this component's own translated validation authoritative:
            type="email" would otherwise raise the browser's own untranslated bubble and
            handleLogin would never run.
            Enter in a field now submits; that is intended. */}
        {mode === 'login' && (
          <form onSubmit={handleLoginSubmit} noValidate>
            <div className="space-y-4">
              {/* Email Address */}
              <div>
                <label htmlFor="signin-email" className="block text-sm font-medium mb-2" style={{ color: '#024B99' }}>
                  {t('email_label')}
                </label>
                <input
                  type="email"
                  name="email"
                  autoComplete="username"
                  id="signin-email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder={t('email_placeholder')}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  style={{ fontSize: '14px' }}
                />
              </div>

              {/* Password */}
              <div>
                <label htmlFor="signin-password" className="block text-sm font-medium mb-2" style={{ color: '#024B99' }}>
                  {t('password_label')}
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    autoComplete="current-password"
                    id="signin-password"
                    value={formData.password}
                    onChange={handleInputChange}
                    placeholder={t('password_placeholder')}
                    className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    style={{ fontSize: '14px' }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                    style={{ fontSize: '20px', background: 'transparent', border: 'none', cursor: 'pointer' }}
                  >
                    {showPassword ? '👁️' : '👁️‍🗨️'}
                  </button>
                </div>

                {/* Forgot Password Link - immediately below password */}
                <div className="text-left text-sm mt-2">
                  <button
                    type="button"
                    onClick={() => setShowForgotPassword(true)}
                    className="font-semibold hover:underline"
                    style={{ color: '#024B99', background: 'transparent', border: 'none', cursor: 'pointer', padding: 0 }}
                  >
                    {t('forgot_password')}
                  </button>
                </div>
              </div>

              {/* Remember My Email - immediately below Forgot Password */}
              <div className="flex items-start gap-2">
                <input
                  type="checkbox"
                  id="rememberMe"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="mt-1 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <label htmlFor="rememberMe" className="text-sm" style={{ color: '#024B99', cursor: 'pointer' }}>
                  {t('remember_me')}
                </label>
              </div>
            </div>
            <div className="flex gap-4 mt-8 justify-center items-center">
              <button
                type="button"
                onClick={handleBack}
                className="py-3 w-[140px] rounded-lg font-semibold transition-colors leading-snug"
                style={{ background: 'white', border: '2px solid #024B99', color: '#024B99' }}
              >
                {t('continue_browsing')}
              </button>
              <button
                type="submit"
                disabled={loading}
                className="py-3 w-[140px] rounded-lg font-semibold text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ background: '#5DBEA3', border: 'none' }}
              >
                {loading ? t('common:please_wait') : t('common:submit')}
              </button>
            </div>
          </form>
        )}

        {/* Register — one screen. A real <form> (Advisor A14), like sign-in above, so a password
            manager offers to save the new password at the moment the account is created; its
            buttons are raw with explicit type, the documented LAYOUT § Usage Rule exception
            already taken by the sign-in form (StandardButton emits no `type`). */}
        {mode === 'register' && !sentTo && (
          <form onSubmit={handleRegisterSubmit} noValidate>
            {fieldErrors.general && (
              <div role="alert" className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
                {t(fieldErrors.general)}
              </div>
            )}
            <div className="space-y-4">
              <div>
                <label htmlFor="register-email" className="block text-sm font-medium mb-2" style={{ color: '#024B99' }}>
                  {t('email_label')}
                </label>
                <input
                  type="email"
                  name="email"
                  autoComplete="username"
                  id="register-email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder={t('email_placeholder')}
                  aria-invalid={!!fieldErrors.email}
                  aria-describedby={fieldErrors.email ? 'register-email-error' : undefined}
                  className={inputClass}
                  style={{ fontSize: '14px', ...borderFor('email') }}
                />
                {renderFieldError('email')}
              </div>

              <div>
                <label htmlFor="register-display-name" className="block text-sm font-medium mb-2" style={{ color: '#024B99' }}>
                  {t('username_label')}
                </label>
                <input
                  type="text"
                  name="userName"
                  autoComplete="nickname"
                  id="register-display-name"
                  value={formData.userName}
                  onChange={handleInputChange}
                  maxLength={USER_DISPLAY_NAME_MAX}
                  placeholder={t('username_placeholder')}
                  aria-invalid={!!fieldErrors.userName}
                  aria-describedby={fieldErrors.userName ? 'register-userName-error' : undefined}
                  className={inputClass}
                  style={{ fontSize: '14px', ...borderFor('userName') }}
                />
                {renderFieldError('userName')}
              </div>

              <div>
                <label htmlFor="register-password" className="block text-sm font-medium mb-2" style={{ color: '#024B99' }}>
                  {t('password_label')}
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    autoComplete="new-password"
                    id="register-password"
                    value={formData.password}
                    onChange={handleInputChange}
                    placeholder={t('password_placeholder')}
                    aria-invalid={!!fieldErrors.password}
                    aria-describedby={fieldErrors.password
                      ? 'register-password-error'
                      : (formData.password && formData.password.length < PASSWORD_MIN_LENGTH ? 'register-password-hint' : undefined)}
                    className={`${inputClass} pr-12`}
                    style={{ fontSize: '14px', ...borderFor('password') }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                    style={{ fontSize: '20px', background: 'transparent', border: 'none', cursor: 'pointer' }}
                  >
                    {showPassword ? '👁️' : '👁️‍🗨️'}
                  </button>
                </div>
                {/* As the person types: length only, the shared minimum (addendum 2 item 1). */}
                {!fieldErrors.password && formData.password && formData.password.length < PASSWORD_MIN_LENGTH && (
                  <p id="register-password-hint" className="text-sm mt-1" style={{ color: '#6B7280' }}>
                    {t('password_min_length', { min: PASSWORD_MIN_LENGTH })}
                  </p>
                )}
                {renderFieldError('password')}
              </div>

              <div>
                <label htmlFor="register-confirm-password" className="block text-sm font-medium mb-2" style={{ color: '#024B99' }}>
                  {t('confirm_password_label')}
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    name="confirmPassword"
                    autoComplete="new-password"
                    id="register-confirm-password"
                    value={formData.confirmPassword}
                    onChange={handleInputChange}
                    placeholder={t('confirm_password_placeholder')}
                    aria-invalid={!!fieldErrors.confirmPassword}
                    aria-describedby={fieldErrors.confirmPassword ? 'register-confirmPassword-error' : undefined}
                    className={`${inputClass} pr-12`}
                    style={{ fontSize: '14px', ...borderFor('confirmPassword') }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                    style={{ fontSize: '20px', background: 'transparent', border: 'none', cursor: 'pointer' }}
                  >
                    {showPassword ? '👁️' : '👁️‍🗨️'}
                  </button>
                </div>
                {renderFieldError('confirmPassword')}
              </div>

              <div>
                <div className="flex items-start gap-2">
                  <input
                    type="checkbox"
                    id="agreeToTerms"
                    checked={agreeToTerms}
                    onChange={(e) => { setAgreeToTerms(e.target.checked); setFieldErrors(prev => ({ ...prev, terms: null })); }}
                    className="mt-1 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <label htmlFor="agreeToTerms" className="text-sm" style={{ color: '#024B99', cursor: 'pointer' }}>
                    {t('agree_terms_prefix')}{' '}
                    <a href="/tac/TERMS.md" target="_blank" rel="noopener noreferrer" className="underline hover:text-blue-700">{t('terms_of_service')}</a>
                    {' '}{t('and')}{' '}
                    <a href="/tac/PRIVACY.md" target="_blank" rel="noopener noreferrer" className="underline hover:text-blue-700">{t('privacy_policy')}</a>
                  </label>
                </div>
                {renderFieldError('terms')}
              </div>
              <div className="text-center">
                <p className="text-sm font-bold mb-1" style={{ color: '#024B99' }}>{t('terms_heading')}</p>
                <a href="/tac/TERMS.md" target="_blank" rel="noopener noreferrer" className="text-sm hover:underline block" style={{ color: '#024B99' }}>{t('terms_of_service')}</a>
                <a href="/tac/PRIVACY.md" target="_blank" rel="noopener noreferrer" className="text-sm hover:underline block" style={{ color: '#024B99' }}>{t('privacy_policy')}</a>
                <a href="/tac/LICENSE.md" target="_blank" rel="noopener noreferrer" className="text-sm hover:underline block" style={{ color: '#024B99' }}>{t('license')}</a>
              </div>

              <div className="text-center text-sm">
                <span style={{ color: '#666' }}>
                  {t('already_have_account') + ' '}
                </span>
                <button
                  type="button"
                  onClick={switchMode}
                  className="font-semibold hover:underline"
                  style={{ color: '#024B99', background: 'transparent', border: 'none', cursor: 'pointer', padding: 0 }}
                >
                  {t('log_in')}
                </button>
              </div>
            </div>
            <div className="flex gap-4 mt-8 justify-center items-center">
              <button
                type="button"
                onClick={handleBack}
                className="py-3 w-[140px] rounded-lg font-semibold transition-colors leading-snug"
                style={{ background: 'white', border: '2px solid #024B99', color: '#024B99' }}
              >
                {t('continue_browsing')}
              </button>
              <button
                type="submit"
                disabled={loading}
                className="py-3 w-[140px] rounded-lg font-semibold text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed leading-snug"
                style={{ background: '#5DBEA3', border: 'none' }}
              >
                {loading ? t('common:please_wait') : t('wizard_create_account')}
              </button>
            </div>
          </form>
        )}

        {/* Check your email (R3): true wording, a resend that waits Auth's minimum, and a way back
            for a mistyped address. */}
        {mode === 'register' && sentTo && (
          <div className="text-center">
            <h2 className="text-2xl font-bold mb-4" style={{ color: '#024B99' }}>{t('check_email_title')}</h2>
            <p className="mb-6" style={{ color: '#1F2937', fontSize: '15px', lineHeight: '1.5' }}>
              {t('check_email_body', { email: sentTo })}
            </p>
            <div className="flex flex-col gap-3 items-center">
              <div>{renderResend(sentTo, t('check_email_resend'))}</div>
              <button
                type="button"
                onClick={handleStartAgain}
                className="text-sm hover:underline"
                style={{ color: '#024B99', background: 'transparent', border: 'none', cursor: 'pointer', padding: 0 }}
              >
                {t('check_email_start_again')}
              </button>
              <button
                type="button"
                onClick={handleBack}
                className="text-sm hover:underline"
                style={{ color: '#024B99', background: 'transparent', border: 'none', cursor: 'pointer', padding: 0 }}
              >
                {t('continue_browsing')}
              </button>
            </div>
          </div>
        )}

        {canInstall && (
          <div className="text-center text-sm mt-6">
            {/* Bare <button>, not StandardButton variant="inline": matches the modal's sibling text
                links, and fixed-blue per LAYOUT § Color System exception (this modal never reads
                theme.primary). Outside the login <form>, type="button" regardless. */}
            <button
              type="button"
              onClick={install}
              className="font-semibold hover:underline"
              style={{ color: '#024B99', background: 'transparent', border: 'none', cursor: 'pointer', padding: 0 }}
            >
              {t('install_the_app')}
            </button>
          </div>
        )}

        </div>
      </Modal>

      <ForgotPasswordModal
        visible={showForgotPassword}
        onClose={() => setShowForgotPassword(false)}
      />
    </>
  );
};

export default LoginRegisterModal;
